import AddIcon from "@mui/icons-material/Add";
import ClearIcon from "@mui/icons-material/Clear";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import RefreshIcon from "@mui/icons-material/Refresh";
import RemoveIcon from "@mui/icons-material/Remove";
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Box,
  Button,
  Chip,
  IconButton,
  InputAdornment,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import React, { useEffect, useState } from "react";
import { getAllFacilityStatusDisplayNames } from "../../utils/facilityStatusMapping";
import { getAllFacilityTypeDisplayNames } from "../../utils/facilityTypeMapping";
import {
  LABEL_DISPLAY_MAP,
  getFullNamesFromKeys,
  getKeysFromFullNames,
  getLabelAbbreviation,
  getLabelFullName,
} from "../../utils/labelMapping";
import { getAllCategoryDisplayNames } from "../../utils/organizationCategoryMapping";
import FacilityOwnerFilter, {
  FacilityOwnerFilterData,
} from "../Layout/filters/FacilityOwnerFilter";
import FilterSection from "../Layout/filters/FilterSection";
import MultiSelectDropdown from "../Layout/filters/MultiSelectDropdown";
import ProjectOrganizationFilter, {
  ProjectOrganizationFilterData,
} from "../Layout/filters/ProjectOrganizationFilter";
import { RelationshipFilterData } from "../Layout/filters/RelationshipFilter";
import RelationshipFilters from "../Layout/filters/RelationshipFilters";
import GraphSettingsSection, {
  GraphParameters,
} from "../Layout/filters/sections/GraphSettingsSection";
import VirtualizedHSCodeDropdown from "../Layout/filters/VirtualizedHSCodeDropdown";
import VirtualizedNAICSDropdown from "../Layout/filters/VirtualizedNAICSDropdown";
import VirtualizedRegionDropdown from "../Layout/filters/VirtualizedRegionDropdown";
import VirtualizedProjectStatusDropdown from "./VirtualizedProjectStatusDropdown";
import VirtualizedProjectTypeDropdown from "./VirtualizedProjectTypeDropdown";

interface SearchFilters {
  searchTerm: string;
  entityType: "organizations" | "facilities" | "projects" | "sources";
  regions: string[];
  industries: string[];
  facilityTypes: string[];
  organizationTypes: string[];
  projectTypes: string[];
  sourceTypes: string[];
  labels: string[];
  dateRange: {
    start?: string;
    end?: string;
  };
  customFilters: Record<string, any>;
  relationshipFilters: RelationshipFilterData[];
  facilityOwnerFilter: FacilityOwnerFilterData;
  projectOrganizationFilter: ProjectOrganizationFilterData;
}

interface SearchFilterPanelProps {
  // View mode
  viewMode?: "search" | "graph";

  // Search-specific props
  filters?: SearchFilters;
  onFilterChange?: (filterKey: keyof SearchFilters, value: any) => void;
  activeFilterCount?: number;

  // Graph-specific props
  graphParameters?: GraphParameters;
  onGraphParameterChange?: (key: keyof GraphParameters, value: any) => void;
  onGraphRefresh?: () => void;
  graphLoading?: boolean;
  hasParameterChanges?: boolean;

  // Common props
  onClearAll: () => void;
  onSaveSearch?: () => void;
  onLoadSearch?: () => void;
}

const SearchFilterPanel: React.FC<SearchFilterPanelProps> = ({
  viewMode = "search",
  filters,
  onFilterChange,
  onClearAll,
  activeFilterCount = 0,
  onSaveSearch,
  onLoadSearch,
  graphParameters,
  onGraphParameterChange,
  onGraphRefresh,
  graphLoading = false,
  hasParameterChanges = false,
}) => {
  const isGraphView = viewMode === "graph";
  const isSearchView = viewMode === "search";
  // Local state for accordion expansion - initialize based on active filters
  const [expandedAccordions, setExpandedAccordions] = useState<{
    [key: string]: boolean;
  }>(() => ({
    graphSettings: isGraphView, // Always expanded in graph view
    location: (filters?.customFilters.homeCountries?.length || 0) > 0,
    industry:
      (filters?.customFilters.naicsCodes?.length || 0) +
        (filters?.customFilters.inputHSCodes?.length || 0) +
        (filters?.customFilters.outputHSCodes?.length || 0) +
        (filters?.labels?.length || 0) >
      0,
    facility:
      (filters?.facilityTypes?.length || 0) +
        (filters?.customFilters.facilityStatuses?.length || 0) >
      0,
    project:
      (filters?.customFilters.projectTypes?.length || 0) +
        (filters?.customFilters.projectStatuses?.length || 0) >
      0,
    organization:
      (filters?.organizationTypes?.length || 0) +
        (filters?.customFilters.employeeCountMin ? 1 : 0) +
        (filters?.customFilters.employeeCountMax ? 1 : 0) +
        (filters?.customFilters.foundingYearMin ? 1 : 0) +
        (filters?.customFilters.foundingYearMax ? 1 : 0) +
        (filters?.labels?.length || 0) >
        0 ||
      Number(filters?.customFilters.foundingYearMin) > 0 ||
      Number(filters?.customFilters.foundingYearMax) > 0,
    dateRange: !!filters?.dateRange.start || !!filters?.dateRange.end,
    relationships:
      !!filters?.relationshipFilters?.length ||
      !!filters?.facilityOwnerFilter?.ownerCompanies?.length ||
      !!filters?.projectOrganizationFilter?.participantCompanies?.length,
  }));

  // Local state for founding year inputs
  const [localFoundingYearMin, setLocalFoundingYearMin] = useState(
    filters?.customFilters.foundingYearMin || ""
  );
  const [localFoundingYearMax, setLocalFoundingYearMax] = useState(
    filters?.customFilters.foundingYearMax || ""
  );

  // Update local state when filters change externally (e.g., from URL or clear all)
  useEffect(() => {
    if (!filters) return;
    setLocalFoundingYearMin(filters.customFilters.foundingYearMin || "");
    setLocalFoundingYearMax(filters.customFilters.foundingYearMax || "");
  }, [
    filters?.customFilters.foundingYearMin,
    filters?.customFilters.foundingYearMax,
  ]);

  // Handler for accordion expansion (allows manual toggling)
  const handleAccordionChange =
    (panel: string) => (event: React.SyntheticEvent, isExpanded: boolean) => {
      setExpandedAccordions((prev) => ({
        ...prev,
        [panel]: isExpanded,
      }));
    };

  // Helper function to update founding year filters
  const updateFoundingYearFilters = () => {
    if (!filters || !onFilterChange) return;
    onFilterChange("customFilters", {
      ...filters.customFilters,
      foundingYearMin: localFoundingYearMin,
      foundingYearMax: localFoundingYearMax,
    });
  };

  // Helper function to clear founding year filters
  const clearFoundingYearFilters = () => {
    if (!filters || !onFilterChange) return;
    setLocalFoundingYearMin("");
    setLocalFoundingYearMax("");
    onFilterChange("customFilters", {
      ...filters.customFilters,
      foundingYearMin: "",
      foundingYearMax: "",
    });
  };

  // Handle Enter key press for founding year inputs
  const handleFoundingYearKeyPress = (event: React.KeyboardEvent) => {
    if (event.key === "Enter") {
      updateFoundingYearFilters();
    }
  };

  // Helper functions for year increment/decrement
  const incrementYear = (field: "min" | "max") => {
    if (field === "min") {
      const currentValue =
        parseInt(localFoundingYearMin) || new Date().getFullYear() - 10;
      setLocalFoundingYearMin(String(currentValue + 1));
    } else {
      const currentValue =
        parseInt(localFoundingYearMax) || new Date().getFullYear();
      setLocalFoundingYearMax(String(currentValue + 1));
    }
  };

  const decrementYear = (field: "min" | "max") => {
    if (field === "min") {
      const currentValue =
        parseInt(localFoundingYearMin) || new Date().getFullYear() - 10;
      if (currentValue > 1800) {
        setLocalFoundingYearMin(String(currentValue - 1));
      }
    } else {
      const currentValue =
        parseInt(localFoundingYearMax) || new Date().getFullYear();
      if (currentValue > 1800) {
        setLocalFoundingYearMax(String(currentValue - 1));
      }
    }
  };

  const facilityTypes = getAllFacilityTypeDisplayNames();
  const facilityStatuses = getAllFacilityStatusDisplayNames();
  const organizationCategories = getAllCategoryDisplayNames();
  const labelOptions = Object.keys(LABEL_DISPLAY_MAP).map((key) =>
    getLabelFullName(key)
  );

  // Convert between internal keys and display names for labels
  const labelsDisplayValue = filters
    ? getFullNamesFromKeys(filters.labels)
    : [];
  const handleLabelsChange = (displayNames: string[]) => {
    if (!onFilterChange) return;
    const keys = getKeysFromFullNames(displayNames);
    onFilterChange("labels", keys);
  };

  // Function to get abbreviation for chip display
  const getLabelChipText = (fullName: string): string => {
    const key = getKeysFromFullNames([fullName])[0];
    return getLabelAbbreviation(key);
  };

  return (
    <Box
      sx={{
        width: 320,
        height: "100%",
        backgroundColor: "background.paper",
        borderRight: 1,
        borderColor: "divider",
        display: "flex",
        flexDirection: "column",
      }}
    >
      {/* Header */}
      <Box sx={{ p: 3, borderBottom: 1, borderColor: "divider" }}>
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            mb: 2,
          }}
        >
          <Typography variant="h6">Advanced Filters</Typography>
          <Button
            size="small"
            onClick={onClearAll}
            disabled={activeFilterCount === 0}
            startIcon={<ClearIcon />}
          >
            Clear All
          </Button>
        </Box>

        <Typography variant="body2" color="text.secondary">
          {activeFilterCount} active filter{activeFilterCount !== 1 ? "s" : ""}
        </Typography>
      </Box>

      {/* Filter Content */}
      <Box sx={{ flex: 1, p: 2, overflow: "auto" }}>
        {/* Graph Settings Section - Only visible in graph view */}
        {isGraphView && graphParameters && onGraphParameterChange && (
          <GraphSettingsSection
            parameters={graphParameters}
            onParameterChange={onGraphParameterChange}
            expanded={expandedAccordions.graphSettings}
            onChange={handleAccordionChange("graphSettings")}
            disabled={graphLoading}
          />
        )}

        {/* Location Filters */}
        {filters &&
          ["organizations", "facilities", "projects"].includes(
            filters.entityType
          ) && (
            <Accordion
              sx={{
                bgcolor: "background.paper",
                borderBottom: 1,
                borderColor: "divider",
                "&:before": { display: "none" },
              }}
              elevation={0}
              expanded={expandedAccordions.location}
              onChange={handleAccordionChange("location")}
            >
              <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                <Typography variant="subtitle2" fontWeight="medium">
                  Location & Geography
                  {(filters.customFilters.homeCountries?.length || 0) > 0 && (
                    <Chip
                      label={filters.customFilters.homeCountries?.length || 0}
                      size="small"
                      sx={{ ml: 1, height: 20, fontSize: "0.75rem" }}
                    />
                  )}
                </Typography>
              </AccordionSummary>
              <AccordionDetails>
                <FilterSection>
                  <VirtualizedRegionDropdown
                    label="Home Regions & Countries"
                    value={filters.customFilters.homeCountries || []}
                    onChange={(countries) =>
                      onFilterChange?.("customFilters", {
                        ...filters.customFilters,
                        homeCountries: countries,
                      })
                    }
                    placeholder="Search regions and countries..."
                  />
                </FilterSection>
              </AccordionDetails>
            </Accordion>
          )}
        {/* Industry & Business Filters */}
        {filters &&
          ["organizations", "facilities", "projects"].includes(
            filters.entityType
          ) && (
            <Accordion
              sx={{
                bgcolor: "background.paper",
                borderBottom: 1,
                borderColor: "divider",
                "&:before": { display: "none" },
              }}
              elevation={0}
              expanded={expandedAccordions.industry}
              onChange={handleAccordionChange("industry")}
            >
              <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                <Typography variant="subtitle2" fontWeight="medium">
                  Industry & Business
                  {(filters.customFilters.naicsCodes?.length || 0) +
                    (filters.customFilters.inputHSCodes?.length || 0) +
                    (filters.customFilters.outputHSCodes?.length || 0) +
                    (filters.labels?.length || 0) >
                    0 && (
                    <Chip
                      label={
                        (filters.customFilters.naicsCodes?.length || 0) +
                        (filters.customFilters.inputHSCodes?.length || 0) +
                        (filters.customFilters.outputHSCodes?.length || 0) +
                        (filters.labels?.length || 0)
                      }
                      size="small"
                      sx={{ ml: 1, height: 20, fontSize: "0.75rem" }}
                    />
                  )}
                </Typography>
              </AccordionSummary>
              <AccordionDetails>
                <FilterSection>
                  {["organizations", "facilities"].includes(
                    filters.entityType
                  ) && (
                    <FilterSection>
                      <MultiSelectDropdown
                        label="Defense Industry Labels"
                        value={labelsDisplayValue}
                        options={labelOptions}
                        onChange={handleLabelsChange}
                        getChipLabel={getLabelChipText}
                        searchable
                      />
                    </FilterSection>
                  )}
                  <VirtualizedNAICSDropdown
                    selectedCodes={filters.customFilters.naicsCodes || []}
                    onCodesChange={(codes) =>
                      onFilterChange?.("customFilters", {
                        ...filters.customFilters,
                        naicsCodes: codes,
                      })
                    }
                    placeholder="Search NAICS codes..."
                  />
                </FilterSection>
                {["organizations", "facilities"].includes(
                  filters.entityType
                ) && (
                  <FilterSection>
                    <VirtualizedHSCodeDropdown
                      label="Input Products (HS Codes)"
                      selectedCodes={filters.customFilters.inputHSCodes || []}
                      onCodesChange={(codes) =>
                        onFilterChange?.("customFilters", {
                          ...filters.customFilters,
                          inputHSCodes: codes,
                        })
                      }
                      placeholder="Search input HS codes..."
                    />
                  </FilterSection>
                )}
                {["organizations", "facilities"].includes(
                  filters.entityType
                ) && (
                  <FilterSection>
                    <VirtualizedHSCodeDropdown
                      label="Output Products (HS Codes)"
                      selectedCodes={filters.customFilters.outputHSCodes || []}
                      onCodesChange={(codes) =>
                        onFilterChange?.("customFilters", {
                          ...filters.customFilters,
                          outputHSCodes: codes,
                        })
                      }
                      placeholder="Search output HS codes..."
                    />
                  </FilterSection>
                )}
              </AccordionDetails>
            </Accordion>
          )}
        {/* Facility-Specific Filters */}
        {filters && filters.entityType === "facilities" && (
          <Accordion
            sx={{
              bgcolor: "background.paper",
              borderBottom: 1,
              borderColor: "divider",
              "&:before": { display: "none" },
            }}
            elevation={0}
            expanded={expandedAccordions.facility}
            onChange={handleAccordionChange("facility")}
          >
            <AccordionSummary expandIcon={<ExpandMoreIcon />}>
              <Typography variant="subtitle2" fontWeight="medium">
                Facility Details
                {(filters.facilityTypes?.length || 0) +
                  (filters.customFilters.facilityStatuses?.length || 0) >
                  0 && (
                  <Chip
                    label={
                      (filters.facilityTypes?.length || 0) +
                      (filters.customFilters.facilityStatuses?.length || 0)
                    }
                    size="small"
                    sx={{ ml: 1, height: 20, fontSize: "0.75rem" }}
                  />
                )}
              </Typography>
            </AccordionSummary>
            <AccordionDetails>
              <FilterSection>
                <MultiSelectDropdown
                  label="Facility Types"
                  value={filters.facilityTypes || []}
                  options={facilityTypes}
                  onChange={(value) => onFilterChange?.("facilityTypes", value)}
                  searchable
                />
              </FilterSection>

              <FilterSection>
                <MultiSelectDropdown
                  label="Facility Status"
                  value={filters.customFilters.facilityStatuses || []}
                  options={facilityStatuses}
                  onChange={(value) =>
                    onFilterChange?.("customFilters", {
                      ...filters.customFilters,
                      facilityStatuses: value,
                    })
                  }
                  searchable
                />
              </FilterSection>
            </AccordionDetails>
          </Accordion>
        )}

        {/* Project-Specific Filters */}
        {filters && filters.entityType === "projects" && (
          <Accordion
            sx={{
              bgcolor: "background.paper",
              borderBottom: 1,
              borderColor: "divider",
              "&:before": { display: "none" },
            }}
            elevation={0}
            expanded={expandedAccordions.project}
            onChange={handleAccordionChange("project")}
          >
            <AccordionSummary expandIcon={<ExpandMoreIcon />}>
              <Typography variant="subtitle2" fontWeight="medium">
                Project Details
                {(filters.customFilters.projectTypes?.length || 0) +
                  (filters.customFilters.projectStatuses?.length || 0) >
                  0 && (
                  <Chip
                    label={
                      (filters.customFilters.projectTypes?.length || 0) +
                      (filters.customFilters.projectStatuses?.length || 0)
                    }
                    size="small"
                    sx={{ ml: 1, height: 20, fontSize: "0.75rem" }}
                  />
                )}
              </Typography>
            </AccordionSummary>
            <AccordionDetails>
              <FilterSection>
                <VirtualizedProjectTypeDropdown
                  selectedProjectTypes={
                    filters.customFilters.projectTypes || []
                  }
                  onProjectTypesChange={(projectTypes) =>
                    onFilterChange?.("customFilters", {
                      ...filters.customFilters,
                      projectTypes: projectTypes,
                    })
                  }
                  label="Project Types"
                />
              </FilterSection>

              <FilterSection>
                <VirtualizedProjectStatusDropdown
                  selectedStatuses={filters.customFilters.projectStatuses || []}
                  onStatusesChange={(projectStatuses) =>
                    onFilterChange?.("customFilters", {
                      ...filters.customFilters,
                      projectStatuses: projectStatuses,
                    })
                  }
                  label="Project Status"
                />
              </FilterSection>

              {/* <FilterSection>
                <Typography variant="body2" sx={{ mb: 1, fontWeight: 'medium' }}>
                  Budget Range
                </Typography>
                <Stack spacing={1}>
                  <TextField
                    size="small"
                    label="Min Budget (USD)"
                    type="number"
                    value={filters.customFilters.budgetMin || ''}
                    onChange={(e) => onFilterChange('customFilters', {
                      ...filters.customFilters,
                      budgetMin: e.target.value
                    })}
                  />
                  <TextField
                    size="small"
                    label="Max Budget (USD)"
                    type="number"
                    value={filters.customFilters.budgetMax || ''}
                    onChange={(e) => onFilterChange('customFilters', {
                      ...filters.customFilters,
                      budgetMax: e.target.value
                    })}
                  />
                </Stack>
              </FilterSection> */}
            </AccordionDetails>
          </Accordion>
        )}

        {/* Source-Specific Filters */}
        {/* <Accordion>
            <AccordionSummary expandIcon={<ExpandMoreIcon />}>
              <Typography variant="subtitle2" fontWeight="medium">
                Source Details
                {filters.sourceTypes.length > 0 && (
                  <Chip
                    label={filters.sourceTypes.length}
                    size="small"
                    sx={{ ml: 1, height: 20, fontSize: '0.75rem' }}
                  />
                )}
              </Typography>
            </AccordionSummary>
            <AccordionDetails>
              <FilterSection>
                <MultiSelectDropdown
                  label="Source Types"
                  value={filters.sourceTypes}
                  options={sourceTypes}
                  onChange={(value) => onFilterChange('sourceTypes', value)}
                  searchable
                />
              </FilterSection>
            </AccordionDetails>
          </Accordion> */}

        {/* Organization-Specific Filters */}
        {filters && filters.entityType === "organizations" && (
          <Accordion
            sx={{
              bgcolor: "background.paper",
              borderBottom: 1,
              borderColor: "divider",
              "&:before": { display: "none" },
            }}
            elevation={0}
            expanded={expandedAccordions.organization}
            onChange={handleAccordionChange("organization")}
          >
            <AccordionSummary expandIcon={<ExpandMoreIcon />}>
              <Typography variant="subtitle2" fontWeight="medium">
                Organization Details
                {(filters.organizationTypes?.length || 0) +
                  (filters.customFilters.foundingYearMin ||
                  filters.customFilters.foundingYearMax
                    ? 1
                    : 0) >
                  0 && (
                  <Chip
                    label={
                      (filters.organizationTypes?.length || 0) +
                      (filters.customFilters.foundingYearMin ||
                      filters.customFilters.foundingYearMax
                        ? 1
                        : 0)
                    }
                    size="small"
                    sx={{ ml: 1, height: 20, fontSize: "0.75rem" }}
                  />
                )}
              </Typography>
            </AccordionSummary>
            <AccordionDetails>
              <FilterSection>
                <MultiSelectDropdown
                  label="Organization Type"
                  value={filters.organizationTypes || []}
                  options={organizationCategories}
                  onChange={(value) =>
                    onFilterChange?.("organizationTypes", value)
                  }
                  searchable
                />
              </FilterSection>
              <FilterSection>
                <Typography
                  variant="body2"
                  sx={{ mb: 1, fontWeight: "medium" }}
                >
                  Founding Year Range
                </Typography>
                <Stack spacing={1}>
                  <TextField
                    size="small"
                    label="From Year"
                    type="number"
                    value={localFoundingYearMin}
                    onChange={(e) => setLocalFoundingYearMin(e.target.value)}
                    onKeyPress={handleFoundingYearKeyPress}
                    placeholder="e.g. 1990"
                    InputProps={{
                      endAdornment: (
                        <InputAdornment position="end">
                          <Stack
                            direction="column"
                            spacing={0}
                            sx={{
                              border: 0,
                              borderColor: "divider",
                              borderRadius: "4px",
                              overflow: "hidden",
                            }}
                          >
                            <IconButton
                              size="small"
                              onClick={() => incrementYear("min")}
                              sx={{
                                padding: "2px",
                                borderRadius: 0,
                                borderBottom: 1,
                                borderColor: "divider",
                                "&:hover": { bgcolor: "action.hover" },
                              }}
                            >
                              <AddIcon sx={{ fontSize: 14 }} />
                            </IconButton>
                            <IconButton
                              size="small"
                              onClick={() => decrementYear("min")}
                              sx={{
                                padding: "2px",
                                borderRadius: 0,
                                "&:hover": { bgcolor: "action.hover" },
                              }}
                            >
                              <RemoveIcon sx={{ fontSize: 14 }} />
                            </IconButton>
                          </Stack>
                        </InputAdornment>
                      ),
                    }}
                    sx={{
                      "& input[type=number]": {
                        MozAppearance: "textfield",
                      },
                      "& input[type=number]::-webkit-outer-spin-button": {
                        WebkitAppearance: "none",
                        margin: 0,
                      },
                      "& input[type=number]::-webkit-inner-spin-button": {
                        WebkitAppearance: "none",
                        margin: 0,
                      },
                    }}
                  />
                  <TextField
                    size="small"
                    label="To Year"
                    type="number"
                    value={localFoundingYearMax}
                    onChange={(e) => setLocalFoundingYearMax(e.target.value)}
                    onKeyPress={handleFoundingYearKeyPress}
                    placeholder="e.g. 2020"
                    InputProps={{
                      endAdornment: (
                        <InputAdornment position="end">
                          <Stack
                            direction="column"
                            spacing={0}
                            sx={{
                              border: 0,
                              borderColor: "divider",
                              borderRadius: "4px",
                              overflow: "hidden",
                            }}
                          >
                            <IconButton
                              size="small"
                              onClick={() => incrementYear("max")}
                              sx={{
                                padding: "2px",
                                borderRadius: 0,
                                borderBottom: 1,
                                borderColor: "divider",
                                "&:hover": { bgcolor: "action.hover" },
                              }}
                            >
                              <AddIcon sx={{ fontSize: 14 }} />
                            </IconButton>
                            <IconButton
                              size="small"
                              onClick={() => decrementYear("max")}
                              sx={{
                                padding: "2px",
                                borderRadius: 0,
                                "&:hover": { bgcolor: "action.hover" },
                              }}
                            >
                              <RemoveIcon sx={{ fontSize: 14 }} />
                            </IconButton>
                          </Stack>
                        </InputAdornment>
                      ),
                    }}
                    sx={{
                      "& input[type=number]": {
                        MozAppearance: "textfield",
                      },
                      "& input[type=number]::-webkit-outer-spin-button": {
                        WebkitAppearance: "none",
                        margin: 0,
                      },
                      "& input[type=number]::-webkit-inner-spin-button": {
                        WebkitAppearance: "none",
                        margin: 0,
                      },
                    }}
                  />
                  <Stack direction="row" spacing={1} sx={{ mt: 1 }}>
                    <Button
                      variant="outlined"
                      size="small"
                      onClick={updateFoundingYearFilters}
                      disabled={
                        localFoundingYearMin ===
                          (filters.customFilters.foundingYearMin || "") &&
                        localFoundingYearMax ===
                          (filters.customFilters.foundingYearMax || "")
                      }
                      sx={{ flex: 1 }}
                    >
                      Update
                    </Button>
                    <Button
                      variant="outlined"
                      size="small"
                      onClick={clearFoundingYearFilters}
                      disabled={
                        !localFoundingYearMin &&
                        !localFoundingYearMax &&
                        !filters.customFilters.foundingYearMin &&
                        !filters.customFilters.foundingYearMax
                      }
                      sx={{ flex: 1 }}
                    >
                      Clear
                    </Button>
                  </Stack>
                </Stack>
              </FilterSection>
            </AccordionDetails>
          </Accordion>
        )}

        {/* Date Range Filters */}
        {filters && (
          <Accordion
            sx={{
              bgcolor: "background.paper",
              borderBottom: 1,
              borderColor: "divider",
              "&:before": { display: "none" },
            }}
            elevation={0}
            expanded={expandedAccordions.dateRange}
            onChange={handleAccordionChange("dateRange")}
          >
            <AccordionSummary expandIcon={<ExpandMoreIcon />}>
              <Typography variant="subtitle2" fontWeight="medium">
                Updated Date Range
                {(filters.dateRange.start || filters.dateRange.end) && (
                  <Chip
                    label="1"
                    size="small"
                    sx={{ ml: 1, height: 20, fontSize: "0.75rem" }}
                  />
                )}
              </Typography>
            </AccordionSummary>
            <AccordionDetails>
              <FilterSection>
                <Stack spacing={2}>
                  <TextField
                    size="small"
                    label="Start Date"
                    type="date"
                    value={filters.dateRange.start || ""}
                    onChange={(e) =>
                      onFilterChange?.("dateRange", {
                        ...filters.dateRange,
                        start: e.target.value,
                      })
                    }
                    InputLabelProps={{ shrink: true }}
                  />
                  <TextField
                    size="small"
                    label="End Date"
                    type="date"
                    value={filters.dateRange.end || ""}
                    onChange={(e) =>
                      onFilterChange?.("dateRange", {
                        ...filters.dateRange,
                        end: e.target.value,
                      })
                    }
                    InputLabelProps={{ shrink: true }}
                  />
                </Stack>
              </FilterSection>
            </AccordionDetails>
          </Accordion>
        )}

        {/* Relationship Filters */}
        {filters &&
          ["organizations", "facilities", "projects"].includes(
            filters.entityType
          ) && (
            <Accordion
              sx={{
                bgcolor: "background.paper",
                borderBottom: 1,
                borderColor: "divider",
                "&:before": { display: "none" },
              }}
              elevation={0}
              expanded={expandedAccordions.relationships}
              onChange={handleAccordionChange("relationships")}
            >
              <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                <Typography variant="subtitle2" sx={{ fontWeight: "medium" }}>
                  Relationships
                  {(filters.relationshipFilters?.length || 0) +
                    (filters.facilityOwnerFilter?.ownerCompanies?.length || 0) +
                    (filters.projectOrganizationFilter?.participantCompanies
                      ?.length || 0) >
                    0 && (
                    <Chip
                      label={
                        (filters.relationshipFilters?.length || 0) +
                        (filters.facilityOwnerFilter?.ownerCompanies?.length ||
                          0) +
                        (filters.projectOrganizationFilter?.participantCompanies
                          ?.length || 0)
                      }
                      size="small"
                      sx={{ ml: 1, height: 20, fontSize: "0.75rem" }}
                    />
                  )}
                </Typography>
              </AccordionSummary>
              {filters.entityType === "organizations" && (
                <AccordionDetails>
                  <RelationshipFilters
                    filters={filters.relationshipFilters}
                    onChange={(newFilters) =>
                      onFilterChange?.("relationshipFilters", newFilters)
                    }
                  />
                </AccordionDetails>
              )}
              {filters.entityType === "facilities" && (
                <AccordionDetails>
                  <FilterSection>
                    <FacilityOwnerFilter
                      filter={
                        filters.facilityOwnerFilter || {
                          ownerCompanies: [],
                          maxTiers: 1,
                        }
                      }
                      onChange={(updatedFilter) =>
                        onFilterChange?.("facilityOwnerFilter", updatedFilter)
                      }
                    />
                  </FilterSection>
                </AccordionDetails>
              )}
              {filters.entityType === "projects" && (
                <AccordionDetails>
                  <FilterSection>
                    <ProjectOrganizationFilter
                      filter={
                        filters.projectOrganizationFilter || {
                          participantCompanies: [],
                          maxTiers: 1,
                        }
                      }
                      onChange={(updatedFilter) =>
                        onFilterChange?.(
                          "projectOrganizationFilter",
                          updatedFilter
                        )
                      }
                    />
                  </FilterSection>
                </AccordionDetails>
              )}
            </Accordion>
          )}
      </Box>

      {/* Save/Load Actions - Fixed at Bottom */}
      <Box
        sx={{
          position: "sticky",
          bottom: 0,
          backgroundColor: "background.paper",
          borderTop: 1,
          borderColor: "divider",
          zIndex: 10,
        }}
      >
        {onSaveSearch && onLoadSearch && (
          <Box
            sx={{
              p: 2,
              borderBottom: isGraphView ? 1 : 0,
              borderColor: "divider",
            }}
          >
            <Stack direction="row" spacing={1}>
              <Button
                fullWidth
                variant="outlined"
                size="medium"
                onClick={onLoadSearch}
              >
                Load Search
              </Button>
              <Button
                fullWidth
                variant="contained"
                size="medium"
                onClick={onSaveSearch}
                disabled={activeFilterCount === 0}
              >
                Save Search
              </Button>
            </Stack>
          </Box>
        )}

        {/* Refresh Button - Graph View Only */}
        {isGraphView && onGraphRefresh && (
          <Box sx={{ p: 2 }}>
            <Button
              fullWidth
              variant="contained"
              size="large"
              onClick={onGraphRefresh}
              disabled={
                graphLoading ||
                !graphParameters?.entityId ||
                !hasParameterChanges
              }
              startIcon={<RefreshIcon />}
              sx={{
                py: 1.5,
                fontWeight: 600,
              }}
            >
              {graphLoading
                ? "Loading..."
                : hasParameterChanges
                  ? "Update Graph"
                  : "Refresh Graph"}
            </Button>
          </Box>
        )}
      </Box>
    </Box>
  );
};

export default SearchFilterPanel;
